import { combineReducers } from "@reduxjs/toolkit";
import { api } from "_shared/_stores/api";
import assistantTurnProgressSlice from "ai-assistant/_stores/assistant-turn-progress-redux-slice";
import connectionHealthReducer from "_shared/_stores/realtime/connection-health-redux-slice";
import { documentsSlice } from "documents/_stores/documents-redux-slice";
import { entityMembersSlice } from "_shared/entity-members/_stores/entity-members-redux-slice";
import proposalsSlice from "work/proposals/_store/proposals-redux-slice";
import proposalCollaborationSlice from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { entityAgreementsSlice } from "legal-entities/agreements/_stores/entity-agreements-redux-slice";
import userEntityMemberInvitationsSlice from "legal-entities/invitations/member-invitations/email-based/_stores/user-entity-member-invitation-redux-store";
import openEntityMemberInvitationsSlice from "legal-entities/invitations/member-invitations/open/_stores/open-entity-member-invitations-redux-slice";
import officerInvitationsSlice from "legal-entities/invitations/officer-invitations/_stores/entity-officer-invitations-redux-slice";
import { bookmarksSlice } from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import { companiesSlice } from "marketplace/companies/_store/companies-redux-slice";
import { individualsSlice } from "marketplace/individuals/_stores/individuals-redux-slice";
import { marketplaceTeamsSlice } from "marketplace/teams/_store/marketplace-teams-redux-slice";
import { feeSchedulesSlice } from "marketplace/teams/fee-schedules/_stores/fee-schedule-redux-slice";
import messagesSlice from "messaging/_stores/messages-redux-slice";
import attachmentsSlice from "messaging/attachments/_stores/message-attachments-redux-slice";
import forumsSlice from "messaging/forums/_stores/forums-redux-slice";
import forumUnreadCountsSlice from "messaging/forums/_stores/forum-unread-counts-redux-slice";
import notificationsSlice from "notifications/_stores/notifications-redux-slice";
import permissionsSlice from "permissions/_stores/permissions-redux-slice";
import feeScheduleTemplatesSlice from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import workFeeSchedulesSlice from "work/fee-schedules/_store/fee-schedules-redux-slice";
import commentsSlice from "work/proposals/comments/_store/comments-redux-slice";
import commentThreadsSlice from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import teamTemplatesSlice from "work/team-templates/_store/team-templates-redux-slice";
import workAgreementsSlice from "work/work-agreements/_store/work-agreement-redux-slice";
import { marketplaceSlice } from "marketplace/_stores/marketplace-redux-slice";
import { userCoworkersSlice } from "users/information/coworker-information/_stores/user-coworkers-redux-slice";
import { networkConnectionsSlice } from "users/networking/_stores/network-connections-redux-slice";
import { networkInvitationsSlice } from "users/networking/_stores/network-invitations-redux-slice";
import { principalChanged } from "users/__session/principal-change";

const combinedReducer = combineReducers({
  [api.reducerPath]: api.reducer,

  connectionHealth: connectionHealthReducer,

  // AI Assistant
  assistantTurnProgress: assistantTurnProgressSlice.reducer,

  // Documents
  documents: documentsSlice.reducer,

  // Legal Entities
  legalEntityAgreements: entityAgreementsSlice.reducer,
  entityOfficerInvitations: officerInvitationsSlice.reducer,
  entityMembers: entityMembersSlice.reducer,
  openEntityMemberInvitations: openEntityMemberInvitationsSlice.reducer,
  userEntityMemberInvitations: userEntityMemberInvitationsSlice.reducer,

  // Marketplace
  bookmarks: bookmarksSlice.reducer,
  individuals: individualsSlice.reducer,
  companies: companiesSlice.reducer,
  teams: marketplaceTeamsSlice.reducer,
  feeSchedules: feeSchedulesSlice.reducer,

  // Messaging
  forums: forumsSlice.reducer,
  forumUnreadCounts: forumUnreadCountsSlice.reducer,
  messages: messagesSlice.reducer,
  attachments: attachmentsSlice.reducer,

  // Permissions
  permissions: permissionsSlice.reducer,

  // Notifications
  notifications: notificationsSlice.reducer,

  // Users
  userCoworkers: userCoworkersSlice.reducer,
  networkConnections: networkConnectionsSlice.reducer,
  networkInvitations: networkInvitationsSlice.reducer,

  // Work
  proposals: proposalsSlice.reducer,
  proposalCollaboration: proposalCollaborationSlice.reducer,
  workAgreements: workAgreementsSlice.reducer,
  feeScheduleTemplates: feeScheduleTemplatesSlice.reducer,
  // This is the work-domain fee schedules state. The shorter `feeSchedules`
  // root key is already used by marketplace team fee schedules above.
  workFeeSchedules: workFeeSchedulesSlice.reducer,
  teamTemplates: teamTemplatesSlice.reducer,

  commentThreads: commentThreadsSlice.reducer,
  comments: commentsSlice.reducer,

  // Marketplace
  marketplace: marketplaceSlice.reducer,
});

/**
 * Authentication principal changes are a hard client-state boundary. Reinitialize every
 * principal-owned cache, but retain the runtime state for middleware instances that stay mounted.
 * Ably updates the existing connection registry while it reconnects under the new identity. RTK
 * Query is reset through its public action by principalChangeReduxMiddleware; preserving its config
 * here keeps the still-mounted middleware registered.
 */
export const rootReducer: typeof combinedReducer = (state, action) => {
  if (!principalChanged.match(action)) return combinedReducer(state, action);

  // Normalize a potentially partial preloaded state before selecting the infrastructure slices.
  const currentState = combinedReducer(state, action);
  const resetState = combinedReducer(undefined, action);
  const resetApiState = api.reducer(currentState[api.reducerPath], api.util.resetApiState());
  return {
    ...resetState,
    connectionHealth: currentState.connectionHealth,
    [api.reducerPath]: {
      ...resetApiState,
      config: currentState[api.reducerPath].config,
    },
  };
};

export type RootState = ReturnType<typeof rootReducer>;
